import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(username, email, password, i18n.language);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      const message = error.response?.data?.detail || t('common.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1633994079794-e7c76a0bf575?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
            </div>
            <span className="font-heading font-bold text-2xl">MOTO SOCIAL</span>
          </div>
          <h2 className="font-heading font-bold text-4xl uppercase tracking-tight">
            {t('landing.hero.title')}
          </h2>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('nav.home')}
          </Link>

          {/* Logo for mobile */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
            </div>
            <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
          </div>

          <h1 className="font-heading font-bold text-3xl uppercase tracking-tight">
            {t('auth.register.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="link-to-login">
              {t('auth.register.login')}
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username">{t('auth.register.username')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="RiderX"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 bg-zinc-950/50 border-zinc-800 focus:border-primary"
                  required
                  minLength={3}
                  data-testid="register-username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.register.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="rider@motoconnect.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-zinc-950/50 border-zinc-800 focus:border-primary"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.register.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-zinc-950/50 border-zinc-800 focus:border-primary"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 brand-glow font-bold uppercase tracking-wider"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.register.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
